import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatErr } from "../lib/api";
import { Wordmark } from "../components/Wordmark";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F5F5F7]">
      <div className="hidden lg:flex relative bg-[#0A0807] text-white p-14 flex-col justify-between overflow-hidden">
        <div className="absolute top-1/3 -left-20 h-[420px] w-[420px] rounded-full bg-[#E08A1E]/15 blur-[100px]" />
        <Wordmark dark className="text-3xl relative" />
        <div className="relative">
          <h1 className="text-6xl font-bold leading-[0.98] tracking-tight">Hire it.<br />Grow it.<br />Pay it<span className="text-[#E08A1E]">.</span></h1>
          <p className="mt-8 text-neutral-400 max-w-sm leading-relaxed">The people platform for the team behind every cup. Attendance, leave, payroll, hiring and reviews in one place.</p>
        </div>
        <div className="text-xs tracking-widest text-neutral-600 relative">MYBEV TECHNOLOGIES · GURUGRAM, INDIA</div>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <Wordmark className="text-3xl lg:hidden" />
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#E08A1E] mt-8">Welcome back</div>
          <h2 className="text-3xl font-extrabold tracking-tight mt-2">Sign in to HRMS</h2>
          <p className="text-sm text-[#6E6E73] mt-2">Use the credentials shared by your HR team.</p>
          <div className="space-y-4 mt-8">
            <div className="space-y-1.5">
              <Label htmlFor="email">Work email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className="h-11 bg-white rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="h-11 bg-white rounded-xl" />
            </div>
            {error && <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={busy} data-testid="login-submit-button" className="w-full h-11 rounded-xl bg-[#E08A1E] hover:bg-[#C87515] text-white font-semibold shadow-[0_8px_20px_rgba(224,138,30,0.28)]">
              {busy ? "Signing in..." : "Sign in"} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
